import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgElement, WithProperties } from '@angular/elements';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { AppState } from '../../core';
import { activity_status, PathTestMapPoint, MapDevice } from '../../core/models';
import { PathTestDoc } from '../../core/schema/path-test-schema';
import {LookupService, ScoutPointInfo, ScoutPointPhotoService, ScoutTaskService, AntennaService, BuildingFloorCombination} from '../../core/services';
import { PathPlotterComponent } from '../../shared/mapping/path-plotter/path-plotter.component';
import { PathTestPointMarkerPopupComponent } from './path-test-point-marker-popup/path-test-point-marker-popup.component';
import { ScoutPointRefLauncher } from '../../core/services/scout-point-ref-launcher';

@Component({
  selector: 'app-path-test-plotter',
  templateUrl: '../../shared/mapping/path-plotter/path-plotter.component.html',
  styleUrls: ['../../shared/mapping/path-plotter/path-plotter.component.scss']
})
export class PathTestPlotterComponent extends PathPlotterComponent<PathTestMapPoint>
    implements OnInit, OnDestroy {

  @Input('mapPoints') set mapPointsSetter( mapPoints: PathTestMapPoint[] ) {
      // Subscribe to antennas whenever map points change (only trigger here)
      this.subscribeToAntennasForBuildingStageFloor(mapPoints)
      this.plotWhenReady(mapPoints)
  }

  @Input('selectedMapPoint') set selectedMapPointSetter( mapPoint: PathTestMapPoint ) {
    this.setSelectedPoint(mapPoint)
  }

  @Input('pathTest')
  pathTest : PathTestDoc

  antennaDevicesSubscription: Subscription

  constructor(
      appState: AppState,
      lookupService: LookupService,
      photoService: ScoutPointPhotoService,
      protected scoutService: ScoutTaskService,
      modalService: BsModalService,
      protected scoutPointRefLauncher: ScoutPointRefLauncher,
      protected antennaService: AntennaService) {
    super(appState,lookupService,photoService,modalService)
    this.markerPopupInitFn = this.pathTestPointMarkerPopupInitFn
  }

  ngOnInit(): void {
    super.ngOnInit()
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.antennaDevicesSubscription?.unsubscribe()
  }

  getColorFn = (point:PathTestMapPoint,index:number) => point.color

  buildLabel( pt: PathTestMapPoint, context: {isViaPt:boolean, isHaPt: boolean }) {
    var labelTxt = super.buildLabel(pt,context)
    const testPtOrder = pt?.pathTestPoint?.order
    return testPtOrder ? `(#${testPtOrder}) ${labelTxt}` : labelTxt
  }

  @Input() scoutPtInfoByPtId: { [ptId:string]:ScoutPointInfo };

  override isMuted(pt: PathTestMapPoint) {
    const isMuted = super.isMuted(pt) ||
      this.scoutPtInfoByPtId?.[pt.pointId||pt.exPointId]?.scoutPoint?.disabled ||
      ( pt?.pathTestPoint && [activity_status.Id.NOT_USING,activity_status.Id.ARCHIVED].includes(pt.pathTestPoint.statusId));
    return isMuted;
  }

  pathTestPointMarkerPopupInitFn = (startPoint:PathTestMapPoint) => {
    return (fl) => {
      const popupComp: NgElement & WithProperties<PathTestPointMarkerPopupComponent> = document.createElement('app-path-test-point-marker-popup-element') as any;
      popupComp.addEventListener('closed', () => document.body.removeChild(popupComp));
      popupComp.pt = startPoint
      popupComp.selectedPt = this.points[this.selectedIndex]
      popupComp.changeGuard = this.changeGuard
      popupComp.deleteScoutPointFn = this.deletePointFn
      popupComp.setSelectedPointFn = this.setSelectedPoint
      popupComp.bShowSelectButton = this.canSelectViaPoints || startPoint.type != 'VIA'
      popupComp.showRefDataFn = this.showRefDataFn
      popupComp.pathTestDoc = this.pathTest
      popupComp.allowEditMode = this.allowEditMode
      popupComp.locked = this.locked
      document.body?.appendChild(popupComp);
      const rtn : any = popupComp
      return rtn;
    }
  }

  // plotPathTest( pathTest : PathTestDoc, pathMapPoints : PathTestMapPoint[] ) {
  //   this.pathTest = pathTest
  //   this.subscribeToAntennasForBuildingStageFloor(pathMapPoints)
  //   this.plot(pathMapPoints)
  // }

  // plotPathTestWhenReady( pathTest : PathTestDoc, pathMapPoints : PathTestMapPoint[] ) {
  //   this.pathTest = pathTest
  //   this.subscribeToAntennasForBuildingStageFloor(pathMapPoints)
  //   this.plotWhenReady(pathMapPoints)
  // }

  showRefDataFn = async ( point : PathTestMapPoint ) => {
    await this.scoutPointRefLauncher.showScoutPointRefData({
      buildingId: this.pathTest.buildingId,
      pathId: this.pathTest.pathId,
      pointId: point?.pointId,
      stage: this.pathTest.stage
    })
  }

  private subscribeToAntennasForBuildingStageFloor(pathMapPoints: PathTestMapPoint[]) {
    console.log('[PathTestPlotter] Subscribing to antennas for path test', {pathTest: this.pathTest, mapPoints: pathMapPoints})

    // Unsubscribe from previous antenna subscription
    this.antennaDevicesSubscription?.unsubscribe()
    
    // Clear existing devices
    this.devices = []
    
    if (!this.pathTest?.stage || !pathMapPoints?.length) {
      console.log('[PathTestPlotter] Missing stage or pathMapPoints for antenna subscription')
      return
    }

    // Extract unique building/floor combinations from the map points
    const buildingFloorMap = new Map<string, BuildingFloorCombination>()
    
    for (const mapPoint of pathMapPoints) {
      if (mapPoint.point?.buildingId) {
        const key = `${mapPoint.point.buildingId}_${mapPoint.point.floorName || mapPoint.point.floorNum || ''}`
        if (!buildingFloorMap.has(key)) {
          buildingFloorMap.set(key, {
            buildingId: mapPoint.point.buildingId,
            floorName: mapPoint.point.floorName || undefined,
            floorNumber: mapPoint.point.floorNum ? parseInt(mapPoint.point.floorNum) : undefined
          })
        }
      }
    }

    const buildingFloors = Array.from(buildingFloorMap.values())
    
    if (buildingFloors.length === 0) {
      console.log('[PathTestPlotter] No valid building/floor combinations found in map points')
      return
    }

    const params = {
      buildingFloors,
      stage: this.pathTest.stage
    }

    try {
      const antennaObservable = this.antennaService.getAntennasMulti$(params)
      
      this.antennaDevicesSubscription = antennaObservable.subscribe({
        next: (antennas) => {
          this.devices = antennas.map(antennaDoc => MapDevice.fromAntenna(antennaDoc))
          // console.log(`[PathTestPlotter] Updated devices from antenna subscription: ${antennas.length} antennas for ${buildingFloors.length} building/floor combinations`, params)
        },
        error: (e: any) => {
          const msg = `Failed to get antennas for path test ${JSON.stringify(params)}: ${e.message}`
          console.error('[PathTestPlotter] Antenna subscription error.', msg, e)
          this.antennaService.publishErrorObj(e, msg, this)
          this.devices = []
        }
      })
      
    } catch (e: any) {
      const msg = `Failed to subscribe to antennas for path test ${JSON.stringify(params)}: ${e.message}`
      console.error('[PathTestPlotter] Failed to create antenna subscription.', msg, e)
      this.antennaService.publishErrorObj(e, msg, this)
    }
  }

}
